/**
 * `useLiveTranscription`
 *
 * Subscribes to Zoom Live Transcription (LTT) `caption-message` events,
 * batches them on the client to avoid a write-storm against the middleware,
 * and POSTs each batch to `/scribe/sessions/:encounterId/transcript`.
 * Phase 2 item 2.9 of the AI Scribe rollout.
 *
 * Returns the in-memory caption list so the eventual `<TranscriptStream>`
 * (item 2.10) can render it directly without a second source of truth.
 *
 * Batching strategy
 *  ------------------
 *  We accumulate captions in a ref and flush either:
 *    (a) when the batch reaches FLUSH_CHUNK_THRESHOLD captions, or
 *    (b) after FLUSH_DEBOUNCE_MS of inactivity since the last caption.
 *
 *  The debounce approach is preferred over per-caption posts because Zoom
 *  LTT can fire several `caption-message` events per second on a chatty
 *  conversation (one per ASR segment, plus a "done" event), and a thundering
 *  herd of POSTs would burn middleware capacity for no clinical gain. The
 *  threshold guard is the back-pressure safety net so a long monologue still
 *  trickles upstream before the user pauses.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useCallSession } from '@/components/session'
import { useClient } from '@/hooks/useClient'
import { logError, logWarning } from '@/utils/errorUtils'
import { isStaleScribeSession404 } from './scribeErrors'

/**
 * Flush the in-memory buffer when this many captions have accumulated, even
 * if the debounce timer hasn't fired. Picked at 6 because Zoom emits roughly
 * one chunk per 1-2 seconds of speech on average — 6 ≈ a normal sentence.
 */
const FLUSH_CHUNK_THRESHOLD = 6

/**
 * Debounce window. 500ms is long enough to merge a typical Zoom ASR burst
 * (often 2-3 partials within ~200ms) but short enough that the doctor's
 * draft view stays "live" feeling.
 */
const FLUSH_DEBOUNCE_MS = 500

export type LiveCaption = {
  text: string
  speaker?: string
  timestamp: string
  /**
   * Zoom LTT sentence id. Real-Zoom captions carry it so the display can
   * update a growing partial in place; seeded (recovery) captions omit it.
   */
  msgId?: string
}

export type UseLiveTranscriptionResult = {
  captions: LiveCaption[]
  isStreaming: boolean
  /** Drain the pending caption buffer now. Best-effort: resolves without throwing, so finalize can `await` it. */
  flushPending: () => Promise<void>
}

type ZoomCaptionMessage = {
  msgId: string
  userId: number
  displayName: string
  text: string
  source: number
  language: number
  timestamp: number
  done?: boolean
}

/**
 * Minimal shape of the zoom client we touch. Avoid importing
 * `@zoom/videosdk` types directly so this module stays cheap to import
 * (the lazy SDK load happens inside CallSessionProvider).
 */
type ZoomClientLike = {
  getLiveTranscriptionClient: () => {
    startLiveTranscription?: () => Promise<unknown> | unknown
    setSpeakingLanguage?: (
      language: string,
      options?: { mode: string },
    ) => Promise<unknown> | unknown
  }
  getCurrentUserInfo: () => { userId: number } | undefined
  on: (event: 'caption-message', listener: (payload: ZoomCaptionMessage) => void) => void
  off: (event: 'caption-message', listener: (payload: ZoomCaptionMessage) => void) => void
}

/**
 * Live-transcription glue for the active call session. Returns the running
 * caption list and a streaming flag so the consumer can show a "live" badge.
 *
 * Caller responsibilities
 *  - Mount this hook only when the Zoom session is in fact active. The hook
 *    guards on `useCallSession().isJoined` itself, but a parent that mounts
 *    it conditionally avoids paying the effect cost when no call is open.
 *
 * Session id
 *  - For Phase 2 we don't yet drive `/scribe/sessions/:id/start`
 *    (item 2.15 owns that mutation). Until then, we send `sessionId` equal
 *    to the encounterId so the middleware buffer is at least keyed
 *    deterministically per encounter. Once `useStartScribe` is wired, the
 *    caller will pass the real opaque sessionId via `sessionId` override.
 */
export function useLiveTranscription({
  sessionId,
  seedCaptions,
}: {
  sessionId?: string
  /**
   * Interrupted-visit recovery: prior transcript lines fetched from
   * `GET /scribe/sessions/:encId/:sessionId/chunks`. Rendered into the
   * transcript pane ONCE on the first render they're provided (when the live
   * caption buffer is still empty), so the doctor sees the conversation that
   * was already captured before they rejoined. New live captions append after
   * these. Seeded lines are display-only — they're already persisted on the
   * server, so we never re-POST them.
   */
  seedCaptions?: LiveCaption[]
} = {}): UseLiveTranscriptionResult {
  // Read `scribeSessionId` from the provider so the POSTs reference the real
  // backend-issued UUID (set by `useScribeSession.start` after /scribe/.../
  // start). The previous fallback to `currentEncounterId` was wrong — the
  // backend keys sessions by the opaque UUID returned from /start, not by
  // the encounter id, and using the wrong id 404s every transcript POST.
  const {
    zoomClient,
    isJoined,
    currentEncounterId,
    scribeSessionId,
    scribeState,
    setScribeSessionId,
    isLocalUserPhysician,
    classifyCallParticipant,
    encounterLanguage,
  } = useCallSession()
  const client = useClient()

  // PD-1600 — the LTT effect below must not re-subscribe on a language change
  // (its deps are deliberately narrow), so it reads the language through a ref
  // and a separate effect re-pins a running session when the language changes
  // (recovery resolves it after the join).
  const languageRef = useRef(encounterLanguage)
  languageRef.current = encounterLanguage
  const lttRef = useRef<ReturnType<ZoomClientLike['getLiveTranscriptionClient']> | null>(null)
  useEffect(() => {
    if (!isJoined || !lttRef.current) return
    Promise.resolve(
      lttRef.current.setSpeakingLanguage?.(encounterLanguage, { mode: 'session' }),
    ).catch((err) => logError('scribe-live-transcription', 'setSpeakingLanguage', err))
  }, [encounterLanguage, isJoined])

  const [captions, setCaptions] = useState<LiveCaption[]>([])
  const [isStreaming, setIsStreaming] = useState(false)

  // Interrupted-visit recovery — seed the transcript pane with the prior
  // (server-persisted) chunks exactly once PER adopted scribe session.
  //
  // Why key on the session id and not a plain one-shot ref: `<CallPage>` is a
  // single instance for the whole `/call/:encounterId/*` subtree, and neither
  // `<EncounterWorkspace>` nor `<ZoomPanel>` remounts when the doctor switches
  // to a different encounter (e.g. resumes a second interrupted visit). A
  // never-resetting `hasSeededRef` would then refuse to seed the 2nd
  // encounter's chunks (or, worse, leave the 1st encounter's captions on
  // screen). We instead track which session id we last seeded for and re-arm
  // the seed (clearing stale captions) when the session id changes to a
  // DIFFERENT non-null id — i.e. a genuine session swap.
  //
  // The `prev !== null` guard is load-bearing: a normal fresh start transitions
  // `scribeSessionId` from `null` → uuid once `/start` resolves. We must NOT
  // wipe captions on that benign first assignment (the doctor may already have
  // live captions buffered in UI state before the id seats). We only clear when
  // moving OFF a previously-seated session onto a new one.
  const seededForSessionRef = useRef<string | null>(null)
  const prevSessionIdRef = useRef<string | null>(null)
  useEffect(() => {
    const current = scribeSessionId ?? null
    const prev = prevSessionIdRef.current
    prevSessionIdRef.current = current
    if (prev === current) return
    // Two distinct transitions must drop the prior session's captions and
    // re-arm the seed:
    //
    //   1. Genuine session swap (non-null → a DIFFERENT non-null id) — the
    //      doctor jumped straight from one seated session to another.
    //
    //   2. Session RESET (non-null → null) — switching patients goes
    //      `uuidA → null → uuidB` because a fresh JOIN hard-resets
    //      `scribeSessionId` to null (`callSessionReducer.ts` JOIN case). The
    //      `null` leg is the PHI-critical one: without clearing here, Patient
    //      A's captions persist into Patient B's pane (neither the swap guard
    //      nor the benign first-assignment below trips). We clear on the
    //      non-null → null edge and re-arm the seed so the next session
    //      (Patient B, or a recovered resume) starts from an empty buffer.
    //
    // We deliberately do NOT clear on the benign first assignment
    // (`null → uuid`): a normal fresh start seats the id after `/start`
    // resolves, and the doctor may already have live captions buffered in UI
    // state before the id seats. A genuine resume (adopting a recovered
    // sessionId via the JOIN `resumeSessionId` path) also lands on this
    // `null → uuid` edge, so its legitimately-restored seedCaptions survive —
    // the seed effect below re-seeds them because `seededForSessionRef` was
    // re-armed to null on the preceding reset edge.
    //
    // The non-null → null clear is gated on `!isJoined` (real call teardown).
    // The stale-session-404 recovery (`flushPending` below) also nulls
    // `scribeSessionId` but WHILE THE CALL IS STILL JOINED — that must NOT wipe
    // the on-screen live transcript (the doctor just re-clicks Start AI Scribe
    // and keeps going). Only a genuine teardown (leaveSession → RESET, which
    // flips `isJoined` false in the same dispatch) clears.
    const realTeardown = current === null && !isJoined
    if ((prev !== null && current !== null && prev !== current) || realTeardown) {
      seededForSessionRef.current = null
      setCaptions([])
    }
  }, [scribeSessionId, isJoined])
  useEffect(() => {
    // Already seeded for this session id — don't double-insert on a re-render
    // that passes the same (or a stable empty) `seedCaptions` reference.
    if (seededForSessionRef.current === (scribeSessionId ?? null)) return
    if (!seedCaptions || seedCaptions.length === 0) return
    seededForSessionRef.current = scribeSessionId ?? null
    // Only seed while the live buffer is still empty so a late-arriving
    // `seedCaptions` can't clobber captions already captured for this session.
    setCaptions((current) => (current.length === 0 ? [...seedCaptions] : current))
  }, [seedCaptions, scribeSessionId])

  // Pending batch + timer live in refs to avoid re-render cycles per caption.
  const pendingRef = useRef<LiveCaption[]>([])
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /**
   * Set of scribe session ids we've already 404'd against. Used to keep the
   * "session vanished" warning at exactly one log line per dead session id,
   * even when two debounce-flushed batches race to the same 404 before the
   * provider's `scribeSessionId = null` flip has propagated through React.
   */
  const stale404SessionIdsRef = useRef<Set<string>>(new Set())

  const flushPending = useCallback(async () => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current)
      flushTimerRef.current = null
    }
    const batch = pendingRef.current
    // Skip the POST entirely when there's no active scribe session (the
    // doctor hasn't clicked "Start AI Scribe" yet). The captions still
    // queue in local state for the UI but we don't try to persist them.
    const effectiveSessionId = sessionId ?? scribeSessionId
    if (batch.length === 0 || !currentEncounterId || !effectiveSessionId) {
      return
    }
    pendingRef.current = []

    // Coalesce consecutive same-speaker captions into one chunk per run (one
    // POST each, newline-joined — OpenAPI takes one chunk/POST) so a
    // doctor+patient turn caught in one window keeps correct per-speaker labels.
    const runs: { speaker?: string; text: string; timestamp: string }[] = []
    for (const c of batch) {
      const last = runs[runs.length - 1]
      if (last && last.speaker === c.speaker) {
        last.text += `\n${c.text}`
        last.timestamp = c.timestamp
      } else {
        runs.push({ speaker: c.speaker, text: c.text, timestamp: c.timestamp })
      }
    }

    let runIndex = 0
    try {
      for (; runIndex < runs.length; runIndex++) {
        const run = runs[runIndex]
        await client.scribeAppendTranscript({
          encounterId: currentEncounterId,
          sessionId: effectiveSessionId,
          text: run.text,
          speaker: run.speaker,
          timestamp: run.timestamp,
        })
      }
    } catch (err) {
      // Stale-session 404: the middleware restarted (HMR in dev, deploy in
      // prod) and our in-memory `sessionId` no longer exists on the server.
      // Without recovery the browser console floods with one error per batch
      // (Zoom LTT emits ~1 caption/sec, so ~10/sec on a chatty conversation),
      // and Sentry would pile up duplicate exceptions.
      //
      // Recovery strategy: clear the provider's `scribeSessionId` so future
      // batches short-circuit on the early-return guard above
      // (`!effectiveSessionId`). The doctor will see the "Start AI Scribe"
      // button reappear via `scribeState` propagation; clicking it mints a
      // fresh session id and the stream resumes cleanly. The user-facing
      // notification is owned by `useFinalizeScribe` (which already handles
      // the same shape on End → Create Progress Note), so we don't double-
      // toast here. We log a single warning per dead session id for Sentry
      // visibility but never an error — this is a recoverable, expected
      // failure mode of the in-memory buffer design.
      //
      // See `isStaleScribeSession404` in `./scribeErrors` for the detection
      // rationale.
      if (isStaleScribeSession404(err) && !sessionId) {
        // Only auto-recover when the hook is using the provider-owned session
        // id (the production code path). If the caller passed an explicit
        // `sessionId` prop they own that lifecycle and we shouldn't reach
        // into the provider on their behalf.
        if (!stale404SessionIdsRef.current.has(effectiveSessionId)) {
          stale404SessionIdsRef.current.add(effectiveSessionId)
          logWarning(
            'scribe-live-transcription',
            'Scribe session vanished on server — clearing local session id',
            `sessionId=${effectiveSessionId}`,
          )
        }
        setScribeSessionId(null)
        return
      }
      // Transient (non-404) error: re-queue the unsent runs so the next flush
      // retries them rather than dropping the tail of the window.
      pendingRef.current = [...runs.slice(runIndex), ...pendingRef.current]
      logError('scribe-live-transcription', 'POST /scribe/.../transcript', err)
    }
  }, [client, currentEncounterId, sessionId, scribeSessionId, setScribeSessionId])

  /**
   * Queue a caption for persistence (POST batch) WITHOUT touching the display
   * list. Real-Zoom partials update the display separately and only land here
   * once final — otherwise every growing partial of a sentence would be stored
   * as its own transcript line.
   */
  const persistCaption = useCallback(
    (caption: LiveCaption) => {
      pendingRef.current = [...pendingRef.current, caption]

      // Threshold trigger: flush immediately if the buffer is full.
      if (pendingRef.current.length >= FLUSH_CHUNK_THRESHOLD) {
        void flushPending()
        return
      }

      // Otherwise (re)start the debounce timer.
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current)
      }
      flushTimerRef.current = setTimeout(() => {
        void flushPending()
      }, FLUSH_DEBOUNCE_MS)
    },
    [flushPending],
  )

  /**
   * Insert-or-replace the display line for a Zoom sentence. Zoom LTT streams
   * a growing partial per sentence (same `msgId` re-emitted as the ASR
   * extends it), so appending each event would stack "Hello" / "Hello, can
   * you" / "Hello, can you hear me?" as separate lines.
   */
  const upsertCaption = useCallback((caption: LiveCaption & { msgId: string }) => {
    setCaptions((current) => {
      for (let i = current.length - 1; i >= 0; i--) {
        if (current[i].msgId === caption.msgId) {
          const next = current.slice()
          next[i] = caption
          return next
        }
      }
      return [...current, caption]
    })
  }, [])

  // Real Zoom LTT effect — subscribes to caption-message and forwards.
  // Detaches when the doctor pauses (`'paused'`) or ends the session
  // (`'finalizing'`) so no further captions are captured. The cleanup persists
  // in-flight sentences and flushes the last debounce window, so pausing keeps
  // everything captured up to that point without recording past it.
  useEffect(() => {
    // Scribe runs only on the treating physician's client — a non-physician
    // (or unresolved role) never starts LTT nor buffers captions.
    if (isLocalUserPhysician !== true) {
      return
    }
    if (!isJoined || !zoomClient || scribeState === 'paused' || scribeState === 'finalizing') {
      return
    }

    const client = zoomClient as unknown as ZoomClientLike

    // Local Zoom user is the provider; everyone else the patient. Classify by
    // userId, not display name (real patient names break name heuristics).
    // getCurrentUserInfo can throw before the SDK settles, so guard it; the
    // handler re-resolves lazily so a momentary miss self-heals instead of
    // killing transcription or inverting attribution.
    let selfUserId: number | undefined
    try {
      selfUserId = client.getCurrentUserInfo()?.userId
    } catch (err) {
      logError('scribe-live-transcription', 'getCurrentUserInfo (speaker attribution)', err)
    }

    let lttClient: ReturnType<ZoomClientLike['getLiveTranscriptionClient']> | undefined
    try {
      lttClient = client.getLiveTranscriptionClient()
      // Start transcription if available. The Zoom SDK requires this call
      // even on the receiver side to enable caption-message events.
      const maybeStart = lttClient.startLiveTranscription?.()
      // Some Zoom SDK versions return a Promise; swallow errors here since
      // the captions event will fail loudly on its own if LTT is disabled.
      Promise.resolve(maybeStart)
        .then(() => {
          // Pin ONE speaking language for the whole session (Zoom's default is
          // per-user, and an unpinned session degraded medical capture); the
          // provider picks it before joining (PD-1600), English otherwise.
          lttRef.current = lttClient ?? null
          return lttClient?.setSpeakingLanguage?.(languageRef.current, { mode: 'session' })
        })
        .catch((err) => {
          logError('scribe-live-transcription', 'startLiveTranscription', err)
        })
    } catch (err) {
      logError('scribe-live-transcription', 'getLiveTranscriptionClient', err)
      return
    }

    // Latest non-final partial per Zoom sentence id. A sentence normally
    // ends with a `done: true` event (which is when we persist it); anything
    // still in flight at teardown is persisted then so the tail of the
    // conversation isn't lost if Zoom never closed the sentence.
    const inFlight = new Map<string, LiveCaption>()

    const handler = (payload: ZoomCaptionMessage) => {
      // Ignore empty payloads — Zoom sometimes emits one when LTT initializes.
      if (!payload?.text?.trim()) {
        return
      }
      if (selfUserId === undefined) {
        try {
          selfUserId = client.getCurrentUserInfo()?.userId
        } catch {
          // SDK still settling; retry on the next caption.
        }
      }
      const caption: LiveCaption = {
        msgId: payload.msgId,
        text: payload.text,
        // LTT runs only on the physician's client, so self ⇒ 'doctor'. A second
        // physician would also read as 'care coordinator' — accepted limitation.
        speaker:
          payload.userId === selfUserId
            ? 'doctor'
            : classifyCallParticipant(payload.userId) === 'staff'
              ? 'care coordinator'
              : 'patient',
        timestamp: new Date(payload.timestamp).toISOString(),
      }
      // Display: live-update the growing partial in place.
      upsertCaption(caption as LiveCaption & { msgId: string })
      // Persistence: only the final form of the sentence.
      if (payload.done) {
        inFlight.delete(payload.msgId)
        persistCaption(caption)
      } else {
        inFlight.set(payload.msgId, caption)
      }
      setIsStreaming(true)
    }

    client.on('caption-message', handler)
    setIsStreaming(true)

    return () => {
      try {
        client.off('caption-message', handler)
      } catch (err) {
        // Best-effort detach; the session may already be torn down.
        logError('scribe-live-transcription', 'detach caption-message', err)
      }
      // Persist any sentences Zoom never marked done (tail of the call).
      for (const caption of inFlight.values()) {
        persistCaption(caption)
      }
      inFlight.clear()
      setIsStreaming(false)
      // Final flush so anything buffered in the last debounce window lands
      // on the server.
      void flushPending()
      lttRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJoined, zoomClient, scribeState, isLocalUserPhysician])

  return { captions, isStreaming, flushPending }
}
